import { useCallback, useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  ProjectStoredFile,
  deleteProjectFile,
  deleteProjectMeetingFile,
  getProjectFileDownloadUrl,
  getProjectMeetingFileDownloadUrl,
  linkProjectFile,
  linkProjectMeetingFile,
  listProjectFiles,
  listProjectMeetingFiles,
  uploadProjectFile,
  uploadProjectMeetingFile,
} from '../../services/commercialProjectsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { PageCard } from '../layout/page/PageCard';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';

type ProjectFilesSectionProps = {
  projectId: string;
  meetingId?: string;
  canWrite: boolean;
  title: string;
  description: string;
};

export function ProjectFilesSection({
  projectId,
  meetingId,
  canWrite,
  title,
  description,
}: ProjectFilesSectionProps) {
  const { confirm } = useConfirmDialog();
  const [items, setItems] = useState<ProjectStoredFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState<'Upload' | 'Link'>('Upload');
  const [fileTitle, setFileTitle] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = meetingId
        ? await listProjectMeetingFiles(projectId, meetingId)
        : await listProjectFiles(projectId);
      setItems(next);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar arquivos.'));
    } finally {
      setLoading(false);
    }
  }, [meetingId, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAdd(): Promise<void> {
    setSaving(true);
    try {
      if (source === 'Upload') {
        if (!file) {
          toast.error('Selecione um arquivo.');
          return;
        }
        if (meetingId) {
          await uploadProjectMeetingFile(projectId, meetingId, file, fileTitle, 'Document');
        } else {
          await uploadProjectFile(projectId, file, fileTitle);
        }
      } else {
        if (!fileTitle.trim() || !url.trim()) {
          toast.error('Informe o nome e o link.');
          return;
        }
        if (meetingId) {
          await linkProjectMeetingFile(projectId, meetingId, fileTitle.trim(), url.trim(), 'Document');
        } else {
          await linkProjectFile(projectId, fileTitle.trim(), url.trim());
        }
      }
      setFileTitle('');
      setUrl('');
      setFile(null);
      toast.success('Arquivo registrado.');
      await load();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao registrar arquivo.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleOpen(item: ProjectStoredFile): Promise<void> {
    try {
      if (item.source === 'Link' && item.externalUrl) {
        window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const download = meetingId
        ? await getProjectMeetingFileDownloadUrl(projectId, meetingId, item.id)
        : await getProjectFileDownloadUrl(projectId, item.id);
      window.open(download.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao abrir o arquivo.'));
    }
  }

  async function handleDelete(item: ProjectStoredFile): Promise<void> {
    const confirmed = await confirm({
      title: 'Excluir',
      message: `Excluir ${item.title}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      if (meetingId) {
        await deleteProjectMeetingFile(projectId, meetingId, item.id);
      } else {
        await deleteProjectFile(projectId, item.id);
      }
      toast.success('Arquivo excluído.');
      await load();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir arquivo.'));
    }
  }

  return (
    <PageCard>
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
          <p className="text-xs text-secondary-500">{description}</p>
        </div>
        {canWrite ? (
          <div className="flex items-end gap-2">
            <label className="erp-label w-36 shrink-0">
              Origem
              <select
                className="erp-input mt-1"
                value={source}
                onChange={(event) => setSource(event.target.value === 'Link' ? 'Link' : 'Upload')}
              >
                <option value="Upload">Upload</option>
                <option value="Link">Link</option>
              </select>
            </label>
            <label className="erp-label min-w-0 flex-1">
              Nome
              <input
                className="erp-input mt-1"
                value={fileTitle}
                maxLength={200}
                onChange={(event) => setFileTitle(event.target.value)}
              />
            </label>
            {source === 'Upload' ? (
              <label className="erp-label min-w-0 flex-1">
                Arquivo
                <input
                  key={items.length}
                  className="erp-input mt-1"
                  type="file"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </label>
            ) : (
              <label className="erp-label min-w-0 flex-1">
                Link
                <input
                  className="erp-input mt-1"
                  value={url}
                  placeholder="https://"
                  onChange={(event) => setUrl(event.target.value)}
                />
              </label>
            )}
            <button
              type="button"
              className="erp-btn-secondary shrink-0 px-3"
              disabled={saving}
              aria-label={saving ? 'Salvando' : 'Adicionar'}
              title={saving ? 'Salvando' : 'Adicionar'}
              onClick={() => void handleAdd()}
            >
              <PlusIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        ) : null}
        {loading ? (
          <LoadingSpinner label="Carregando..." />
        ) : items.length === 0 ? (
          <InlineAlert>Nenhum arquivo.</InlineAlert>
        ) : (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Origem</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>{item.title}</td>
                      <td className="whitespace-nowrap">{item.source === 'Link' ? 'Link' : 'Upload'}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <button type="button" className="erp-btn-secondary" onClick={() => void handleOpen(item)}>
                            Abrir
                          </button>
                          {canWrite ? (
                            <DeleteIconButton label="Excluir arquivo" onClick={() => void handleDelete(item)} />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PageCard>
  );
}
